import type { Company } from '@gat-ship/types'
import * as CompanyEntity from './company-entity'

/**
 * Functional core — collection-level operations on `Company[]`.
 *
 * Every function is pure: it takes the current list (read-only) and returns a
 * brand-new list, never mutating the input and never touching a ref, the
 * network, or loading state. The imperative shell (`useCompanyCollection`)
 * owns the single write point that assigns these results to the cache.
 */

/**
 * Sort companies active-before-inactive, then by name (A→Z). Returns a new
 * array; never mutates the input.
 */
export function sortCompanies(companies: readonly Company[]): Company[] {
  return [...companies].sort((a, b) => {
    if (a.isInactive !== b.isInactive) {
      return a.isInactive ? 1 : -1
    }
    return a.name.localeCompare(b.name)
  })
}

/** Find a company by code. Read-only. */
export function findCompany(
  companies: readonly Company[],
  code: string
): Company | undefined {
  return companies.find((c) => c.code === code)
}

/** Select the active (non-inactive) companies. */
export function activeCompanies(companies: readonly Company[]): Company[] {
  return companies.filter((c) => !c.isInactive)
}

/**
 * Filter companies for a list tab: `'active'` → active only, `'inactive'` →
 * inactive only, any other value → all.
 */
export function filterByTab(
  companies: readonly Company[],
  tab: string
): Company[] {
  if (tab === 'active') {
    return activeCompanies(companies)
  }
  if (tab === 'inactive') {
    return companies.filter((c) => c.isInactive)
  }
  return [...companies]
}

/**
 * Insert-or-replace `company` by code, then re-sort. Unifies the former add
 * and update paths: an existing code is replaced, a new code is appended.
 */
export function upsertCompany(
  companies: readonly Company[],
  company: Company
): Company[] {
  const without = companies.filter((c) => c.code !== company.code)
  return sortCompanies([...without, company])
}

/** Remove the company with `code`. No-op when absent. */
export function removeCompany(
  companies: readonly Company[],
  code: string
): Company[] {
  return companies.filter((c) => c.code !== code)
}

/** Build a new (active) company and insert it into the sorted collection. */
export function addCompany(
  companies: readonly Company[],
  code: string,
  name: string,
  description?: string
): Company[] {
  return upsertCompany(companies, CompanyEntity.create(code, name, description))
}
